import { useState } from 'react';
import {
  Card, Divider, Paper, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ListPageContainer from '../../components/ListPageContainer';
import ConfirmationDialog from '../../components/Dialogs/ConfirmationDialog';
import { ConfirmationDialogType } from '../../consts/enums';
import { PERMISSIONS } from '../../consts/permissions';
import { ReferralForceSendOutcome, ReferralRowAction } from '../../enums/referral.enum';
import { ReferralForceSendResult } from '../../@types/Referral';
import { useAuth } from '../../hooks/useAuth';
import { useTablePageParams } from '../../hooks/useTablePageParams';
import { useGetReferrals, useReferralActions } from '../../hooks/query/useReferrals';
import { Users } from '../../icons/users';
import STRING_KEYS from '../../language/keys';
import ReferralsFilters from './ReferralsFilters';
import { ReferralsTable } from './ReferralsTable';
import { OverrideEligibilityDialog } from './dialogs/OverrideEligibilityDialog';
import { LinkAccountDialog } from './dialogs/LinkAccountDialog';
import { ResendRewardDialog } from './dialogs/ResendRewardDialog';
import { TaxReportDialog } from './dialogs/TaxReportDialog';
import { PendingReferralAction } from './referrals.types';
import {
  getReferralErrorDetail,
  getReferralErrorKind,
  ReferralErrorKind,
  REFERRALS_PAGE_FILTER_KEYS,
  REFERRALS_ROWS_PER_PAGE,
  toReferralsFilters,
} from './referrals.utils';
import style from './style';

export default function ReferralsPage(): JSX.Element {
  const { t: translate } = useTranslation();
  const { hasPermissions } = useAuth();
  const [pendingAction, setPendingAction] = useState<PendingReferralAction | null>(null);
  const [actionError, setActionError] = useState<string | undefined>();
  const [isTaxReportOpen, setIsTaxReportOpen] = useState(false);

  const {
    activeFilters, onChangeFilter, currentPage, onPageChange,
  } = useTablePageParams(REFERRALS_PAGE_FILTER_KEYS);
  const filters = toReferralsFilters(activeFilters);

  const { data, isLoading, error } = useGetReferrals({
    ...filters,
    page: currentPage,
    limit: REFERRALS_ROWS_PER_PAGE,
  });
  const actions = useReferralActions();
  const isProgramDisabled = getReferralErrorKind(error) === ReferralErrorKind.ProgramDisabled;

  // Friendly copy for the known referral errors, otherwise the server's message with its reasons.
  const getErrorMessage = (requestError: unknown): string => {
    switch (getReferralErrorKind(requestError)) {
      case ReferralErrorKind.ProgramDisabled:
        return translate(STRING_KEYS.REFERRALS.PROGRAM_DISABLED);
      case ReferralErrorKind.HubspotUnavailable:
        return translate(STRING_KEYS.REFERRALS.HUBSPOT_UNAVAILABLE);
      default:
        return getReferralErrorDetail(requestError);
    }
  };

  const getForceSendOutcomeMessage = (result: ReferralForceSendResult): string => {
    const outcome = translate(STRING_KEYS.REFERRAL_FORCE_SEND_OUTCOMES[result.outcome]);
    if (!result.reason) {
      return outcome;
    }

    return translate(STRING_KEYS.REFERRALS.FORCE_SEND_OUTCOME_WITH_REASON, { outcome, reason: result.reason });
  };

  const closeDialog = () => setPendingAction(null);

  // Closes the dialog either way, so a failure shows in the page banner instead of behind the modal.
  const run = async (mutation: () => Promise<void>) => {
    setActionError(undefined);
    try {
      await mutation();
    } catch (e) {
      setActionError(translate(STRING_KEYS.REFERRALS.ACTION_ERROR, { message: getErrorMessage(e) }));
    } finally {
      closeDialog();
    }
  };

  const handleAction = (action: PendingReferralAction) => {
    const { referral } = action;
    switch (action.type) {
      case ReferralRowAction.RecheckEligibility:
        run(() => actions.recheckEligibility.mutateAsync(referral.id));
        break;
      case ReferralRowAction.TogglePayingOutsideStripe:
        run(() => actions.updatePayingOutsideStripe.mutateAsync({
          referralId: referral.id,
          payingOutsideStripe: !referral.payingOutsideStripe,
        }));
        break;
      default:
        setActionError(undefined);
        setPendingAction(action);
    }
  };

  const renderDialog = () => {
    if (!pendingAction) {
      return null;
    }

    const { type, referral, targetId } = pendingAction;
    const companyName = referral.referredCompanyName || translate(STRING_KEYS.REFERRALS.UNKNOWN_COMPANY);

    switch (type) {
      case ReferralRowAction.OverrideEligibility:
        return (
          <OverrideEligibilityDialog
            isOpen
            isSubmitting={ actions.overrideEligibility.isLoading }
            onClose={ closeDialog }
            onConfirm={ (reason) => run(() => actions.overrideEligibility.mutateAsync({
              referralId: referral.id,
              reason,
            })) }
          />
        );
      case ReferralRowAction.LinkAccount:
        return (
          <LinkAccountDialog
            isOpen
            companyName={ companyName }
            isSubmitting={ actions.updateReferredAccount.isLoading }
            onClose={ closeDialog }
            onConfirm={ (accountId) => run(() => actions.updateReferredAccount.mutateAsync({
              referralId: referral.id,
              accountId,
            })) }
          />
        );
      case ReferralRowAction.UnlinkAccount:
        return (
          <ConfirmationDialog
            isOpen
            title={ translate(STRING_KEYS.REFERRALS.UNLINK_DIALOG_TITLE) }
            subTitle={ translate(STRING_KEYS.REFERRALS.UNLINK_DIALOG_SUB_TITLE) }
            btnTitle={ translate(STRING_KEYS.REFERRALS.UNLINK_DIALOG_BTN) }
            type={ ConfirmationDialogType.warning }
            onClose={ closeDialog }
            onConfirm={ () => run(() => actions.updateReferredAccount.mutateAsync({
              referralId: referral.id,
              accountId: null,
            })) }
          />
        );
      case ReferralRowAction.Qualify:
        return (
          <ConfirmationDialog
            isOpen
            title={ translate(STRING_KEYS.REFERRALS.QUALIFY_DIALOG_TITLE) }
            subTitle={ translate(STRING_KEYS.REFERRALS.QUALIFY_DIALOG_SUB_TITLE) }
            btnTitle={ translate(STRING_KEYS.REFERRALS.QUALIFY_DIALOG_BTN) }
            type={ ConfirmationDialogType.warning }
            onClose={ closeDialog }
            onConfirm={ () => run(() => actions.qualify.mutateAsync(referral.id)) }
          />
        );
      case ReferralRowAction.ResendReward: {
        const reward = referral.rewards.find((referralReward) => referralReward.id === targetId);
        if (!reward) {
          return null;
        }

        return (
          <ResendRewardDialog
            isOpen
            reward={ reward }
            currentEmail={ reward.deliveryEmail || referral.referrerEmail || '' }
            isSubmitting={ actions.resendReward.isLoading }
            onClose={ closeDialog }
            onConfirm={ (newEmail) => run(() => actions.resendReward.mutateAsync({
              referralRewardId: reward.id,
              newEmail,
            })) }
          />
        );
      }
      case ReferralRowAction.ForceSendReward: {
        const reward = referral.rewards.find((referralReward) => referralReward.id === targetId);

        return (
          <ConfirmationDialog
            isOpen
            title={ translate(STRING_KEYS.REFERRALS.FORCE_SEND_DIALOG_TITLE, {
              milestone: reward?.milestone,
              amount: reward?.amountUsd ? `$${reward.amountUsd}` : '',
            }) }
            subTitle={ translate(STRING_KEYS.REFERRALS.FORCE_SEND_DIALOG_SUB_TITLE) }
            btnTitle={ translate(STRING_KEYS.REFERRALS.FORCE_SEND_DIALOG_BTN) }
            type={ ConfirmationDialogType.warning }
            onClose={ closeDialog }
            onConfirm={ () => run(async () => {
              const result = await actions.forceSendReward.mutateAsync(targetId);
              const isNotSent = !!result?.outcome && result.outcome !== ReferralForceSendOutcome.Sent;
              if (isNotSent) {
                setActionError(getForceSendOutcomeMessage(result));
              }
            }) }
          />
        );
      }
      case ReferralRowAction.EndPosition:
        return (
          <ConfirmationDialog
            isOpen
            title={ translate(STRING_KEYS.REFERRALS.END_POSITION_DIALOG_TITLE) }
            subTitle={ translate(STRING_KEYS.REFERRALS.END_POSITION_DIALOG_SUB_TITLE) }
            btnTitle={ translate(STRING_KEYS.REFERRALS.END_POSITION_DIALOG_BTN) }
            type={ ConfirmationDialogType.error }
            onClose={ closeDialog }
            onConfirm={ () => run(() => actions.endPosition.mutateAsync(targetId)) }
          />
        );
      default:
        return null;
    }
  };

  return (
    <ListPageContainer
      title={ {
        text: translate(STRING_KEYS.LAYOUT_REFERRALS_TITLE),
        icon: <Users />,
      } }
      errors={ actionError }
    >
      { isProgramDisabled ? (
        <Card sx={ style.tableWrapper.sx }>
          <Typography color="textSecondary" align="center" sx={ style.programDisabled.sx }>
            { translate(STRING_KEYS.REFERRALS.PROGRAM_DISABLED) }
          </Typography>
        </Card>
      ) : (
        <Card sx={ style.tableWrapper.sx }>
          <ReferralsFilters
            filters={ filters }
            onChangeFilter={ onChangeFilter }
            onTaxReportClick={ hasPermissions([PERMISSIONS.REFERRAL_MANAGE_REWARDS]) ?
              () => setIsTaxReportOpen(true) :
              undefined }
          />
          <Divider />
          <Paper sx={ style.tableScroll.sx }>
            <ReferralsTable
              referralsData={ data }
              isLoading={ isLoading }
              errorMessage={ error ? getErrorMessage(error) : undefined }
              page={ currentPage }
              rowsPerPage={ REFERRALS_ROWS_PER_PAGE }
              onPageChange={ onPageChange }
              onAction={ handleAction }
            />
          </Paper>
        </Card>
      ) }
      { renderDialog() }
      { isTaxReportOpen && (
        <TaxReportDialog isOpen onClose={ () => setIsTaxReportOpen(false) } />
      ) }
    </ListPageContainer>
  );
}
